import React, { useEffect, useState } from 'react';
import TemplatePreviewEditor from './TemplatePreviewEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const apiGet = async (path, token) => {
  const r = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const text = await r.text();
  try {
    return JSON.parse(text);
  } catch {
    return { detail: `HTTP ${r.status}`, _raw: text.slice(0, 200) };
  }
};
const apiPost = async (path, body, token) => {
  const r = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  try {
    return JSON.parse(text);
  } catch {
    return { success: false, detail: `HTTP ${r.status}`, _raw: text.slice(0, 200) };
  }
};

export default function OutreachTab({ token, language }) {
  const hi = language === 'hi';
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [filter, setFilter] = useState({ district: 'INDORE', only_unsent: false, min_students: '' });
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  // Manual selection of specific schools (checkboxes)
  const [selectedIds, setSelectedIds] = useState([]);
  const [sendingSelected, setSendingSelected] = useState(false);

  // Report filters + export
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportStatus, setReportStatus] = useState('all');
  const [exporting, setExporting] = useState(false);

  // Aayush AI reply assist modal
  const [replyModal, setReplyModal] = useState(null); // {school, suggestion, loading, sending}

  const [batchOpen, setBatchOpen] = useState(false);
  const [batchSize, setBatchSize] = useState(20);
  const [dryRun, setDryRun] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [templateName, setTemplateName] = useState('shiksha_mitra_intro');

  // Approved templates loaded from Meta / our portal
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [presets, setPresets] = useState([]);
  const [submittingPreset, setSubmittingPreset] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [previewPreset, setPreviewPreset] = useState(null);

  const loadStats = async () => {
    try {
      const s = await apiGet('/api/platform-admin/outreach/stats', token);
      setStats(s);
    } catch (e) { console.error(e); }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const [tRes, pRes] = await Promise.all([
        apiGet('/api/whatsapp/templates', token),
        apiGet('/api/whatsapp/templates/presets', token),
      ]);
      if (tRes?.detail || pRes?.detail) {
        setTemplatesError(`API Error: ${tRes?.detail || pRes?.detail}. Login refresh or token issue.`);
      }
      setAvailableTemplates(tRes?.templates || []);
      setPresets(pRes?.presets || []);
    } catch (e) {
      setTemplatesError('Network error: ' + e.message);
      console.error('loadTemplates error:', e);
    }
    setTemplatesLoading(false);
  };

  const submitPresetToMeta = async (presetId) => {
    if (!window.confirm(hi ? `"${presetId}" preset को Meta पर approval के लिए submit करें?` : `Submit "${presetId}" preset to Meta for approval?`)) return;
    setSubmittingPreset(presetId);
    try {
      const r = await apiPost(`/api/whatsapp/templates/submit-preset/${presetId}`, {}, token);
      if (r?.success) {
        alert((hi ? '✅ Submit हो गया! Meta review में है (आमतौर पर 1-24 घंटे)।' : '✅ Submitted to Meta! In review (usually 1-24 hrs).') + `\nTemplate: ${r.template_name || presetId}`);
        await loadTemplates();
      } else {
        alert('Error: ' + (r?.detail || r?.error || 'Unknown'));
      }
    } catch (e) { alert('Network error'); }
    setSubmittingPreset(null);
  };

  const loadList = async (resetOffset = false) => {
    setLoadingList(true);
    const off = resetOffset ? 0 : offset;
    if (resetOffset) setOffset(0);
    try {
      const qs = new URLSearchParams({
        district: filter.district,
        limit: LIMIT,
        offset: off,
        only_with_phone: true,
        only_unsent: filter.only_unsent,
        ...(filter.min_students ? { min_students: filter.min_students } : {}),
      }).toString();
      const r = await apiGet(`/api/platform-admin/outreach/list?${qs}`, token);
      setList(r.schools || []);
      setSelectedIds([]);
    } catch (e) { console.error(e); }
    setLoadingList(false);
  };

  useEffect(() => {
    if (!token) return;
    loadStats();
    loadList();
    loadTemplates();
    // eslint-disable-next-line
  }, [token]);

  const markCalled = async (school) => {
    const note = window.prompt(
      hi ? `📞 ${school.school_name} को call किया? (note add करें)` : `📞 Called ${school.school_name}? (add a note)`,
      ''
    );
    if (note === null) return;
    try {
      const r = await apiPost('/api/platform-admin/outreach/mark-called', { school_id: school.id, note }, token);
      if (r?.success) {
        await loadList();
        await loadStats();
      }
    } catch (e) { alert('Error logging call'); }
  };

  const runBatch = async () => {
    if (!window.confirm(
      dryRun
        ? (hi ? `Dry-run: ${batchSize} schools को fake-send करेगा। कोई WhatsApp message actually नहीं जाएगा। जारी रखें?` : `Dry-run: will fake-send to ${batchSize} schools. No actual WhatsApp sent. Continue?`)
        : (hi ? `⚠️ Real Send: ${batchSize} schools को WhatsApp पर message भेजेगा (₹${(batchSize * 0.88).toFixed(2)} अनुमानित lागत)। जारी रखें?` : `⚠️ Real Send: will send WhatsApp to ${batchSize} schools (approx ₹${(batchSize * 0.88).toFixed(2)} cost). Continue?`)
    )) return;

    setSending(true);
    setSendResult(null);
    try {
      const resp = await apiPost('/api/platform-admin/outreach/send-batch', {
        district: filter.district,
        source_tag: 'indore_private_schools_pdf_2026',
        limit: batchSize,
        offset: 0,
        dry_run: dryRun,
        template_name: templateName,
        language: 'hi',
        only_unsent: true,
      }, token);
      setSendResult(resp);
      await loadStats();
      await loadList(true);
    } catch (e) {
      setSendResult({ success: false, error: String(e) });
    }
    setSending(false);
  };

  const PHONE_OF = (s) => s.phone || s.mobile_number || '—';

  // ---- Manual selection (checkboxes) ----
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === list.length) setSelectedIds([]);
    else setSelectedIds(list.map(s => s.id));
  };

  const sendToSelected = async (dry) => {
    if (selectedIds.length === 0) { alert(hi ? 'पहले schools select करें' : 'Select schools first'); return; }
    if (!dry && !window.confirm(hi ? `⚠️ ${selectedIds.length} चुने हुए schools को real WhatsApp message भेजें?` : `⚠️ Send real WhatsApp to ${selectedIds.length} selected schools?`)) return;
    setSendingSelected(true);
    setSendResult(null);
    try {
      const resp = await apiPost('/api/platform-admin/outreach/send-selected', {
        school_ids: selectedIds,
        template_name: templateName,
        language: 'hi',
        dry_run: dry,
      }, token);
      setSendResult(resp);
      await loadStats();
      await loadList();
    } catch (e) {
      setSendResult({ success: false, error: String(e) });
    }
    setSendingSelected(false);
  };

  // ---- Report export (CSV / PDF / Print) ----
  const exportReport = async (format) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        format,
        district: filter.district,
        status: reportStatus,
        ...(reportFrom ? { date_from: reportFrom } : {}),
        ...(reportTo ? { date_to: reportTo } : {}),
      }).toString();
      const r = await fetch(`${API_URL}/api/platform-admin/outreach/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) { alert('Export failed'); setExporting(false); return; }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outreach_report.${format === 'pdf' ? 'pdf' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Export error: ' + e.message); }
    setExporting(false);
  };

  const printReport = async () => {
    // Open the PDF export in a new tab for printing
    setExporting(true);
    try {
      const qs = new URLSearchParams({
        format: 'pdf',
        district: filter.district,
        status: reportStatus,
        ...(reportFrom ? { date_from: reportFrom } : {}),
        ...(reportTo ? { date_to: reportTo } : {}),
      }).toString();
      const r = await fetch(`${API_URL}/api/platform-admin/outreach/export?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => { try { w.print(); } catch { /* */ } }, 800);
    } catch (e) { alert('Print error'); }
    setExporting(false);
  };

  // ---- Aayush AI reply assist ----
  const openReplyAssist = async (school) => {
    setReplyModal({ school, suggestion: '', loading: true, sending: false });
    try {
      const r = await apiPost('/api/platform-admin/outreach/suggest-reply', {
        school_id: school.id,
        incoming_message: school.outreach_reply || null,
      }, token);
      setReplyModal({ school, suggestion: r.suggestion || '', loading: false, sending: false });
    } catch (e) {
      setReplyModal({ school, suggestion: '', loading: false, sending: false, error: String(e) });
    }
  };

  const sendReply = async () => {
    if (!replyModal?.suggestion?.trim()) return;
    setReplyModal(m => ({ ...m, sending: true }));
    try {
      const r = await apiPost('/api/platform-admin/outreach/send-reply', {
        school_id: replyModal.school.id,
        text: replyModal.suggestion.trim(),
      }, token);
      if (r?.success) {
        alert(hi ? '✅ Reply भेज दिया गया!' : '✅ Reply sent!');
        setReplyModal(null);
        await loadList();
      } else {
        alert((hi ? 'भेजने में त्रुटि: ' : 'Send error: ') + (r?.error || r?.detail || 'Unknown'));
        setReplyModal(m => ({ ...m, sending: false }));
      }
    } catch (e) {
      alert('Network error');
      setReplyModal(m => ({ ...m, sending: false }));
    }
  };

  return (
    <div className="space-y-6" data-testid="outreach-tab-content">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 rounded-2xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">📣 {hi ? 'Launch Outreach — Indore Private Schools' : 'Launch Outreach — Indore Private Schools'}</h2>
        <p className="text-white/90 text-sm">
          {hi ? 'WhatsApp template-based transparent outreach। हर message में data source और opt-out link साथ।' : 'Transparent WhatsApp outreach. Every message includes data source & opt-out link.'}
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="outreach-stats">
          <StatTile label={hi ? 'कुल Schools' : 'Total Schools'} value={stats.total_schools} color="blue" />
          <StatTile label={hi ? 'Phone के साथ' : 'With Phone'} value={stats.with_phone} color="purple" />
          <StatTile label={hi ? 'Sent ✅' : 'Sent ✅'} value={stats.sent} color="green" />
          <StatTile label={hi ? 'Pending' : 'Pending'} value={stats.pending} color="orange" />
          <StatTile label={hi ? 'Failed ❌' : 'Failed ❌'} value={stats.failed} color="red" />
          <StatTile label={hi ? 'Remove Req' : 'Remove Req'} value={stats.removal_requested} color="slate" />
        </div>
      )}

      {/* Templates Panel — submit presets to Meta for approval */}
      <div className="bg-white rounded-xl shadow p-5 border border-slate-200" data-testid="templates-panel">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-bold text-lg">📋 {hi ? 'WhatsApp Templates (Meta Approval)' : 'WhatsApp Templates (Meta Approval)'}</h3>
          <button onClick={loadTemplates} disabled={templatesLoading} className="text-sm text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50" data-testid="btn-reload-templates">
            {templatesLoading ? '⏳' : '🔄'} {hi ? 'Refresh Templates' : 'Refresh Templates'}
          </button>
        </div>

        {templatesError && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs" data-testid="templates-error">
            ⚠️ {templatesError}
          </div>
        )}

        {availableTemplates.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-green-700 mb-2">✅ {hi ? 'Meta से Approved / Pending Templates' : 'Loaded from Meta'}</p>
            <div className="flex flex-wrap gap-2" data-testid="approved-templates-list">
              {availableTemplates.map((t, i) => (
                <span key={i} className={`px-3 py-1 rounded-full text-xs font-medium border ${
                  t.status === 'APPROVED' ? 'bg-green-50 text-green-700 border-green-200' :
                  t.status === 'PENDING' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
                  t.status === 'REJECTED' ? 'bg-red-50 text-red-700 border-red-200' :
                  'bg-slate-50 text-slate-700 border-slate-200'
                }`}>
                  {t.name} · {t.status || 'unknown'}
                </span>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs font-semibold text-slate-600 mb-2">🚀 {hi ? 'Presets — एक click में Meta पर submit करें' : 'Presets — one-click Meta submission'}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="presets-list">
          {presets.filter(p => ['shiksha_mitra_intro', 'shiksha_mitra_followup'].includes(p.id)).map(p => {
            const alreadyExists = availableTemplates.some(t => t.name === p.name);
            return (
              <div key={p.id} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div>
                    <p className="font-mono text-xs font-semibold text-slate-800">{p.name}</p>
                    <p className="text-[10px] text-slate-500">{p.category} · {p.variables_count} variables{p.has_buttons ? ' · has buttons' : ''}</p>
                  </div>
                  {alreadyExists && (
                    <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✓ Submitted</span>
                  )}
                </div>
                <p className="text-[11px] text-slate-600 whitespace-pre-wrap line-clamp-2">{p.body_text}</p>
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={() => setPreviewPreset(p)}
                    className="text-[11px] bg-blue-500 text-white px-3 py-1 rounded-full hover:bg-blue-600 font-medium"
                    data-testid={`btn-preview-preset-${p.id}`}
                  >
                    👁️ Preview & Edit
                  </button>
                  {!alreadyExists && (
                    <button
                      onClick={() => submitPresetToMeta(p.id)}
                      disabled={submittingPreset === p.id}
                      className="text-[11px] bg-orange-500 text-white px-3 py-1 rounded-full hover:bg-orange-600 disabled:opacity-50 font-medium"
                      data-testid={`btn-submit-preset-${p.id}`}
                    >
                      {submittingPreset === p.id ? '⏳' : '📤'} Quick Submit
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Bar */}
      <div className="bg-white rounded-xl shadow p-5 space-y-4 border border-slate-200" data-testid="outreach-actions">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-bold text-lg">🚀 {hi ? 'Batch भेजें' : 'Send Batch'}</h3>
          <button
            onClick={() => setBatchOpen(!batchOpen)}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium"
            data-testid="btn-toggle-batch"
          >
            {batchOpen ? (hi ? '✕ बंद करें' : '✕ Close') : (hi ? '+ Batch Configure करें' : '+ Configure Batch')}
          </button>
        </div>

        {batchOpen && (
          <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid="batch-config">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Batch Size</label>
                <select
                  value={batchSize}
                  onChange={e => setBatchSize(Number(e.target.value))}
                  className="w-full mt-1 px-3 py-2 border rounded-lg"
                  data-testid="select-batch-size"
                >
                  <option value={10}>10 — Micro Pilot</option>
                  <option value={20}>20 — Pilot</option>
                  <option value={50}>50 — Small Batch</option>
                  <option value={100}>100 — Medium</option>
                  <option value={250}>250 — Large</option>
                  <option value={500}>500 — XL</option>
                  <option value={1000}>1000 — Full Blast</option>
                </select>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600">Template (Meta-approved)</label>
                <select
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border rounded-lg text-sm"
                  data-testid="select-template-name"
                >
                  {availableTemplates.length === 0 && <option value="shiksha_mitra_intro">shiksha_mitra_intro (submit for approval below)</option>}
                  {availableTemplates.map(t => (
                    <option key={t.name} value={t.name}>
                      {t.name} {t.status ? `· ${t.status}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {availableTemplates.length === 0
                    ? (hi ? 'कोई approved template नहीं मिला। नीचे preset से submit करें।' : 'No approved templates. Submit a preset below.')
                    : (hi ? `${availableTemplates.length} templates loaded from Meta` : `${availableTemplates.length} templates loaded from Meta`)}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600">Mode</label>
                <div className="mt-1 flex gap-2">
                  <button
                    onClick={() => setDryRun(true)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${dryRun ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' : 'bg-white border'}`}
                    data-testid="btn-dry-run"
                  >
                    🧪 Dry Run
                  </button>
                  <button
                    onClick={() => setDryRun(false)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${!dryRun ? 'bg-green-100 text-green-800 border-2 border-green-400' : 'bg-white border'}`}
                    data-testid="btn-live-send"
                  >
                    🟢 Live Send
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-200">
              <div className="text-sm">
                <p className="font-medium">
                  {dryRun ? '🧪' : '🟢'} {batchSize} schools · Template: <span className="font-mono text-xs">{templateName}</span>
                </p>
                {!dryRun && (
                  <p className="text-[11px] text-slate-500">
                    {hi ? 'अनुमानित लागत:' : 'Est. cost:'} ~₹{(batchSize * 0.88).toFixed(2)} ({hi ? 'marketing rate' : 'marketing rate'})
                  </p>
                )}
              </div>
              <button
                onClick={runBatch}
                disabled={sending}
                className={`px-5 py-2.5 rounded-full font-bold text-white ${dryRun ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-600 hover:bg-green-700'} disabled:opacity-50`}
                data-testid="btn-send-batch"
              >
                {sending ? (hi ? 'भेज रहे हैं...' : 'Sending...') : (dryRun ? (hi ? '🧪 Dry Run चलाएँ' : '🧪 Run Dry') : (hi ? '🚀 अभी भेजें' : '🚀 Send Now'))}
              </button>
            </div>

            {sendResult && (
              <div className={`rounded-lg p-3 text-sm ${sendResult.success !== false ? 'bg-green-50 text-green-900 border border-green-200' : 'bg-red-50 text-red-900 border border-red-200'}`} data-testid="send-result">
                <p className="font-semibold">
                  {sendResult.dry_run ? '🧪 Dry Run' : '✅ Live Send'} Result: {sendResult.sent} sent · {sendResult.failed} failed · {sendResult.batch_size} attempted
                </p>
                {sendResult.results && sendResult.results.length > 0 && (
                  <ul className="mt-2 text-xs space-y-0.5 max-h-40 overflow-y-auto">
                    {sendResult.results.slice(0, 20).map((r, i) => (
                      <li key={i} className="flex gap-2">
                        <span className={r.status === 'sent' || r.status === 'dry_run' ? 'text-green-600' : 'text-red-600'}>●</span>
                        <span className="truncate">{r.school_name} — {r.phone || '—'} — {r.status}{r.error ? `: ${r.error}` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filter + List */}
      <div className="bg-white rounded-xl shadow p-5 border border-slate-200">
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <h3 className="font-bold text-lg flex-1">🏫 {hi ? 'Schools List' : 'Schools List'}</h3>
          <input
            type="number"
            placeholder={hi ? 'Min students' : 'Min students'}
            value={filter.min_students}
            onChange={e => setFilter({ ...filter, min_students: e.target.value })}
            className="px-3 py-1.5 border rounded-lg text-sm w-32"
            data-testid="input-min-students"
          />
          <label className="text-sm flex items-center gap-2">
            <input
              type="checkbox"
              checked={filter.only_unsent}
              onChange={e => setFilter({ ...filter, only_unsent: e.target.checked })}
              data-testid="check-only-unsent"
            />
            {hi ? 'सिर्फ unsent' : 'Only unsent'}
          </label>
          <button onClick={() => loadList(true)} className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600" data-testid="btn-refresh-list">
            🔄 {hi ? 'Refresh' : 'Refresh'}
          </button>
        </div>

        {/* Selection toolbar — appears when schools are checked */}
        {selectedIds.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="selection-toolbar">
            <span className="text-sm font-semibold text-emerald-800">
              ✓ {selectedIds.length} {hi ? 'schools चुने गए' : 'selected'}
            </span>
            <span className="text-xs text-slate-500">Template: <span className="font-mono">{templateName}</span></span>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => sendToSelected(true)}
                disabled={sendingSelected}
                className="px-3 py-1.5 bg-yellow-500 text-white rounded-full text-xs font-bold hover:bg-yellow-600 disabled:opacity-50"
                data-testid="btn-send-selected-dry"
              >
                🧪 {hi ? 'Dry Run (चुने हुए)' : 'Dry Run (selected)'}
              </button>
              <button
                onClick={() => sendToSelected(false)}
                disabled={sendingSelected}
                className="px-3 py-1.5 bg-green-600 text-white rounded-full text-xs font-bold hover:bg-green-700 disabled:opacity-50"
                data-testid="btn-send-selected-live"
              >
                {sendingSelected ? '⏳...' : `🚀 ${hi ? 'चुने हुए को भेजें' : 'Send to selected'} (${selectedIds.length})`}
              </button>
              <button onClick={() => setSelectedIds([])} className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded-full text-xs" data-testid="btn-clear-selection">
                {hi ? 'Clear' : 'Clear'}
              </button>
            </div>
          </div>
        )}

        {loadingList ? (
          <div className="text-center py-8 text-slate-500">Loading...</div>
        ) : list.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">{hi ? 'कोई school नहीं मिली।' : 'No schools found.'}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="outreach-schools-table">
              <thead className="text-xs bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === list.length && list.length > 0}
                      onChange={toggleSelectAll}
                      data-testid="check-select-all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">School</th>
                  <th className="px-3 py-2 text-left">Area</th>
                  <th className="px-3 py-2 text-left">Principal</th>
                  <th className="px-3 py-2 text-left">Phone</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Reply</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {list.map((s, i) => {
                  const phone = PHONE_OF(s);
                  const telHref = phone && phone !== '—' ? `tel:+91${phone.replace(/\D/g, '').slice(-10)}` : null;
                  const waHref = phone && phone !== '—' ? `https://wa.me/91${phone.replace(/\D/g, '').slice(-10)}?text=${encodeURIComponent('Namaste, Shiksha Mitra से बात कर रहा हूँ।')}` : null;
                  return (
                    <tr key={s.id || i} className={`hover:bg-slate-50 ${selectedIds.includes(s.id) ? 'bg-emerald-50/50' : ''}`} data-testid={`row-school-${s.id}`}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(s.id)}
                          onChange={() => toggleSelect(s.id)}
                          data-testid={`check-school-${s.id}`}
                        />
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-900">{s.school_name}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{s.area || '—'}</td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{s.contact_person_name || s.principal_name || '—'}</td>
                      <td className="px-3 py-2 font-mono text-xs">{phone}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {s.contacted_via_call && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-[10px] font-semibold" title={s.contacted_via_call_at}>📞 called</span>
                          )}
                          {s.outreach_sent ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-semibold" title={s.outreach_sent_at}>
                              ✓ wa {s.outreach_delivery_status || 'sent'}
                            </span>
                          ) : s.outreach_status === 'failed' ? (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-[10px] font-semibold">✗ failed</span>
                          ) : !s.contacted_via_call ? (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-[10px]">pending</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs max-w-[200px]">
                        {s.outreach_replied ? (
                          <div className="space-y-1">
                            <div className="text-emerald-700" title={s.outreach_reply_at}>
                              <span className="font-semibold">💬 </span>
                              <span className="truncate inline-block max-w-[130px] align-bottom">{s.outreach_reply || 'replied'}</span>
                            </div>
                            <button
                              onClick={() => openReplyAssist(s)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-600 text-white text-[10px] rounded-full hover:bg-purple-700 font-semibold"
                              data-testid={`btn-ai-reply-${s.id}`}
                              title={hi ? 'Aayush AI से reply draft करें' : 'Draft reply with Aayush AI'}
                            >
                              ✨ {hi ? 'AI Draft' : 'AI Draft'}
                            </button>
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          {telHref && (
                            <a
                              href={telHref}
                              onClick={() => setTimeout(() => markCalled(s), 1200)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-500 text-white text-[11px] rounded-full hover:bg-blue-600 font-medium"
                              data-testid={`btn-direct-call-${s.id}`}
                              title={hi ? 'Call करें — note भी log होगा' : 'Call & log note'}
                            >
                              📞 {hi ? 'Call' : 'Call'}
                            </a>
                          )}
                          {waHref && (
                            <a
                              href={waHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-500 text-white text-[11px] rounded-full hover:bg-green-600 font-medium"
                              data-testid={`btn-direct-wa-${s.id}`}
                              title={hi ? 'WhatsApp खोलें' : 'Open WhatsApp'}
                            >
                              💬 WA
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 text-sm">
          <button
            onClick={() => { const n = Math.max(0, offset - LIMIT); setOffset(n); setTimeout(loadList, 0); }}
            disabled={offset === 0}
            className="px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40"
            data-testid="btn-prev"
          >
            ← {hi ? 'पिछला' : 'Prev'}
          </button>
          <span className="text-slate-500">Showing {offset + 1}–{offset + list.length}</span>
          <button
            onClick={() => { setOffset(offset + LIMIT); setTimeout(loadList, 0); }}
            disabled={list.length < LIMIT}
            className="px-3 py-1.5 bg-slate-100 rounded-lg disabled:opacity-40"
            data-testid="btn-next"
          >
            {hi ? 'अगला' : 'Next'} →
          </button>
        </div>
      </div>

      {/* Sent Report — Date filters + Export */}
      <div className="bg-white rounded-xl shadow p-5 border border-slate-200" data-testid="report-export-panel">
        <h3 className="font-bold text-lg mb-3">📊 {hi ? 'Sent Report & Export' : 'Sent Report & Export'}</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">{hi ? 'दिनांक से' : 'From date'}</label>
            <input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="report-date-from" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">{hi ? 'दिनांक तक' : 'To date'}</label>
            <input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="report-date-to" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Status</label>
            <select value={reportStatus} onChange={e => setReportStatus(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" data-testid="report-status-filter">
              <option value="all">{hi ? 'सभी' : 'All'}</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="called">Called</option>
            </select>
          </div>
          <div className="flex gap-2 ml-auto">
            <button onClick={() => exportReport('csv')} disabled={exporting} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50" data-testid="btn-export-csv">
              ⬇️ CSV
            </button>
            <button onClick={() => exportReport('pdf')} disabled={exporting} className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 disabled:opacity-50" data-testid="btn-export-pdf">
              ⬇️ PDF
            </button>
            <button onClick={printReport} disabled={exporting} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="btn-print-report">
              🖨️ {hi ? 'Print' : 'Print'}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-400 mt-2">
          {hi ? 'Export में school, phone, status, delivery, reply और timestamps शामिल हैं।' : 'Export includes school, phone, status, delivery, reply & timestamps.'}
        </p>
      </div>

      {/* Recent Logs */}
      {stats?.recent_logs?.length > 0 && (
        <div className="bg-white rounded-xl shadow p-5 border border-slate-200">
          <h3 className="font-bold text-lg mb-3">📜 {hi ? 'हाल के Logs' : 'Recent Logs'}</h3>
          <div className="space-y-1.5 text-xs">
            {stats.recent_logs.map((log, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50">
                <span className={log.status === 'sent' ? 'text-green-500' : 'text-red-500'}>●</span>
                <span className="font-medium truncate flex-1">{log.school_name}</span>
                <span className="font-mono text-slate-500">{log.phone}</span>
                <span className="text-slate-400 text-[10px]">{new Date(log.sent_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aayush AI Reply Assist Modal */}
      {replyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="ai-reply-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-bold text-lg flex items-center gap-2">✨ {hi ? 'Aayush AI Reply' : 'Aayush AI Reply'}</h3>
                <p className="text-xs text-slate-500">{replyModal.school.school_name}</p>
              </div>
              <button onClick={() => setReplyModal(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none" data-testid="btn-close-ai-modal">×</button>
            </div>

            {replyModal.school.outreach_reply && (
              <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                <p className="text-[10px] font-semibold text-slate-400 mb-1">{hi ? 'School का message' : "School's message"}</p>
                💬 {replyModal.school.outreach_reply}
              </div>
            )}

            <label className="text-xs font-medium text-slate-600 block mb-1">{hi ? 'सुझाया गया जवाब (edit कर सकते हैं)' : 'Suggested reply (editable)'}</label>
            {replyModal.loading ? (
              <div className="h-32 flex items-center justify-center text-slate-400 text-sm border rounded-lg">
                ⏳ {hi ? 'Aayush AI draft कर रहा है...' : 'Aayush AI is drafting...'}
              </div>
            ) : (
              <textarea
                value={replyModal.suggestion}
                onChange={e => setReplyModal(m => ({ ...m, suggestion: e.target.value }))}
                rows={5}
                className="w-full px-3 py-2 border rounded-lg text-sm"
                data-testid="ai-reply-textarea"
              />
            )}

            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => openReplyAssist(replyModal.school)}
                disabled={replyModal.loading || replyModal.sending}
                className="text-sm text-purple-600 hover:text-purple-700 font-medium disabled:opacity-50"
                data-testid="btn-regenerate-reply"
              >
                🔄 {hi ? 'फिर से draft करें' : 'Regenerate'}
              </button>
              <div className="flex gap-2">
                <button onClick={() => setReplyModal(null)} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm" data-testid="btn-cancel-reply">
                  {hi ? 'रद्द करें' : 'Cancel'}
                </button>
                <button
                  onClick={sendReply}
                  disabled={replyModal.loading || replyModal.sending || !replyModal.suggestion?.trim()}
                  className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 disabled:opacity-50"
                  data-testid="btn-send-ai-reply"
                >
                  {replyModal.sending ? '⏳...' : `🚀 ${hi ? 'WhatsApp पर भेजें' : 'Send on WhatsApp'}`}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-slate-400 mt-2">
              {hi ? 'Note: WhatsApp policy अनुसार freeform reply school के message के 24 घंटे के अंदर ही जाता है।' : 'Note: Freeform reply works only within 24h of the school\'s message (WhatsApp policy).'}
            </p>
          </div>
        </div>
      )}

      {/* Preview & Edit Modal */}
      {previewPreset && (
        <TemplatePreviewEditor
          preset={previewPreset}
          token={token}
          onClose={() => setPreviewPreset(null)}
          onSubmitted={() => { loadTemplates(); }}
        />
      )}
    </div>
  );
}

function StatTile({ label, value, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    purple: 'bg-purple-50 text-purple-900 border-purple-200',
    green: 'bg-green-50 text-green-900 border-green-200',
    orange: 'bg-orange-50 text-orange-900 border-orange-200',
    red: 'bg-red-50 text-red-900 border-red-200',
    slate: 'bg-slate-50 text-slate-900 border-slate-200',
  }[color] || 'bg-slate-50 text-slate-900 border-slate-200';
  return (
    <div className={`rounded-xl p-3 border ${colors}`} data-testid={`stat-tile-${label}`}>
      <div className="text-2xl font-black">{value ?? '—'}</div>
      <div className="text-[11px] mt-0.5 opacity-80">{label}</div>
    </div>
  );
}
